import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="container" style={{ maxWidth: '800px', margin: '20px auto', padding: '20px' }}>
      <h1>Terms of Service</h1>
      <p><strong>Last Updated: June 2, 2026</strong></p>
      <p>
        Please read these Terms of Service carefully before using ASMR.guru. By accessing or using
        this Service you agree to be bound by these terms. If you do not agree, please do not use
        the Service.
      </p>

      <h2>1. Description of Service</h2>
      <p>
        ASMR.guru is a free video discovery and curation platform for Autonomous Sensory Meridian
        Response (ASMR) content. The Service retrieves publicly available video and channel
        information via the YouTube Data API and displays videos embedded directly from YouTube.
        ASMR.guru does not host, upload, store, or own any video content.
      </p>

      <h2>2. YouTube API Services</h2>
      <p>
        This Service is built using the YouTube API Services operated by Google LLC. By using
        ASMR.guru you also agree to be bound by YouTube&apos;s Terms of Service, available at{' '}
        <a
          href="https://www.youtube.com/t/terms"
          target="_blank"
          rel="noopener noreferrer"
        >
          youtube.com/t/terms
        </a>
        . YouTube&apos;s terms govern your interactions with all YouTube content displayed through
        this platform.
      </p>
      <p>
        Google&apos;s Privacy Policy, available at{' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          policies.google.com/privacy
        </a>
        , also applies to your use of YouTube API Services accessed through ASMR.guru.
      </p>

      <h2>3. Embedded Video Content</h2>
      <p>
        Videos displayed on ASMR.guru are embedded via YouTube&apos;s official iframe embed
        functionality. All video content remains the property of the respective creators and is
        subject to the copyrights and terms under which it was published on YouTube. ASMR.guru
        makes no claim of ownership over any embedded content.
      </p>
      <p>
        Video availability, embeddability, and content are determined entirely by YouTube and the
        individual content creators. We are not responsible for changes to, removal of, or
        unavailability of any specific video.
      </p>
      <p>
        If you believe your copyrighted content is being displayed inappropriately, please contact
        us at{' '}
        <a href="mailto:privacy@asmr.guru">privacy @asmr.guru</a>.
      </p>

      <h2>4. User Accounts</h2>
      <p>
        When user account registration becomes available, only the following information will be
        collected: a username, a password (stored as a secure hash — never in plain text), and an
        email address used solely for account verification. Filter and preference settings you
        configure will be saved to your account. No additional personal data will be solicited
        or retained.
      </p>
      <p>
        You are responsible for maintaining the confidentiality of your account credentials and for
        all activity that occurs under your account.
      </p>

      <h2>5. Google Sign-In</h2>
      <p>
        You will have the option to sign in using your Google account. If you choose this method,
        only your Google account email address will be shared with ASMR.guru and used solely to
        identify your account. We will not access your YouTube account, subscriptions, watch
        history, playlists, or any other Google or YouTube data.
      </p>

      <h2>6. Account Deletion</h2>
      <p>
        You may request deletion of your account and all associated data at any time by emailing{' '}
        <a href="mailto:privacy@asmr.guru">privacy @asmr.guru</a>. All account data will be
        permanently deleted within 30 days of your verified request.
      </p>

      <h2>7. Cookies</h2>
      <p>ASMR.guru uses the following types of cookies:</p>
      <ul>
        <li>
          <strong>Essential:</strong> A session cookie keeps you logged in while your session is
          active (when accounts are available). This cookie is required for authentication and
          cannot be disabled.
        </li>
        <li>
          <strong>Analytics:</strong> Google Analytics cookies measure aggregate site usage.
          These are only set with your explicit consent via the cookie banner.
        </li>
        <li>
          <strong>Advertising:</strong> Google AdSense cookies serve relevant advertisements.
          These are only set with your explicit consent via the cookie banner.
        </li>
      </ul>
      <p>
        You can update your cookie preferences at any time through the consent banner or your
        browser settings. See our <Link to="/privacy">Privacy Policy</Link> for full details.
      </p>

      <h2>8. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use automated bots, scrapers, or tools to access or harvest data from the Service</li>
        <li>Attempt to circumvent any technical restrictions, rate limits, or security measures</li>
        <li>
          Use the Service in any manner that violates YouTube&apos;s{' '}
          <a
            href="https://www.youtube.com/t/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </a>{' '}
          or{' '}
          <a
            href="https://developers.google.com/youtube/terms/developer-policies"
            target="_blank"
            rel="noopener noreferrer"
          >
            Developer Policies
          </a>
        </li>
        <li>Use the Service for any unlawful purpose</li>
        <li>Attempt to reverse-engineer any portion of the Service</li>
      </ul>

      <h2>9. Affiliate Links</h2>
      <p>
        The Gear section of ASMR.guru contains affiliate links. If you purchase a product through
        one of these links, ASMR.guru may receive a small commission at no additional cost to you.
        Affiliate relationships are disclosed on the Gear page.
      </p>

      <h2>10. Disclaimer of Warranties</h2>
      <p>
        ASMR.guru is provided &quot;as is&quot; and &quot;as available&quot; without warranties of
        any kind, express or implied. We do not guarantee uninterrupted access to the Service, the
        accuracy or completeness of video metadata, or the continued availability of any specific
        content. Your use of the Service is at your own risk.
      </p>

      <h2>11. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by applicable law, ASMR.guru and its operators shall not be
        liable for any indirect, incidental, special, consequential, or punitive damages arising
        from your use of, or inability to use, the Service or any content accessed through it.
      </p>

      <h2>12. Third-Party Services</h2>
      <p>
        The Service integrates with third-party services including YouTube (Google LLC), Google
        Analytics, and Google AdSense. Your interactions with those services are governed by their
        respective terms and privacy policies. ASMR.guru is not responsible for the practices of
        any third-party service.
      </p>

      <h2>13. Changes to These Terms</h2>
      <p>
        We may update these Terms of Service from time to time. Material changes will be reflected
        by an updated &quot;Last Updated&quot; date at the top of this page. Continued use of the
        Service after changes are posted constitutes your acceptance of the revised terms.
      </p>

      <h2>14. Governing Law</h2>
      <p>
        These Terms are governed by and construed in accordance with the laws of the State of
        Wyoming, United States, without regard to its conflict of law provisions.
      </p>

      <h2>15. Contact</h2>
      <address style={{ fontStyle: 'normal', lineHeight: '1.8' }}>
        <strong>Email:</strong>{' '}
        <a href="mailto:privacy@asmr.guru">privacy @asmr.guru</a>
        <br />
        <strong>Mail:</strong><br />
        ASMR.guru<br />
        30 N Gould St Ste R<br />
        Sheridan, WY 82801<br />
        United States
      </address>
    </div>
  );
};

export default TermsOfService;
